/**
 * The Strategic Planning Hub frame: a navy rail, a light content field, one primary action per screen.
 *
 * The rail navigates where you are — the current application's own pages, then the directory and
 * administration that every application shares. The switcher in the top bar goes somewhere else.
 * Keeping those two jobs separate is why an application's section only appears from inside it.
 *
 * One audience — OPOC staff. What a user sees is decided by the permissions their roles hold and
 * the applications they hold a role in; the backend refuses anything they reach by URL anyway.
 */
import { useMemo, useState } from 'react';
import type { SvgIconComponent } from '@mui/icons-material';
import ArticleRounded from '@mui/icons-material/ArticleRounded';
import AssignmentIndRounded from '@mui/icons-material/AssignmentIndRounded';
import AssignmentRounded from '@mui/icons-material/AssignmentRounded';
import BusinessRounded from '@mui/icons-material/BusinessRounded';
import DifferenceRounded from '@mui/icons-material/DifferenceRounded';
import FactCheckRounded from '@mui/icons-material/FactCheckRounded';
import TableChartRounded from '@mui/icons-material/TableChartRounded';
import WebRounded from '@mui/icons-material/WebRounded';
import GroupsRounded from '@mui/icons-material/GroupsRounded';
import HistoryRounded from '@mui/icons-material/HistoryRounded';
import HomeRounded from '@mui/icons-material/HomeRounded';
import SlideshowRounded from '@mui/icons-material/SlideshowRounded';
import LayersRounded from '@mui/icons-material/LayersRounded';
import LibraryBooksRounded from '@mui/icons-material/LibraryBooksRounded';
import LogoutRounded from '@mui/icons-material/LogoutRounded';
import MenuRounded from '@mui/icons-material/MenuRounded';
import PeopleRounded from '@mui/icons-material/PeopleRounded';
import SettingsRounded from '@mui/icons-material/SettingsRounded';
import SpaceDashboardRounded from '@mui/icons-material/SpaceDashboardRounded';
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListSubheader,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { PacketReviewQueueButton } from '@/apps/packets/reviewQueue';
import { ReviewQueueButton } from '@/apps/presentations/reviewQueue';
import { useAuth } from '@/auth/AuthProvider';
import { ColorModeMenuItems } from '@/components/common/ColorModeToggle';
import { radius, semantic, surface, tint } from '@/theme/theme';
import { token } from '@/theme/tokens';
import { AppFooter } from '@/platform/shell/AppFooter';
import { AppSwitcher } from '@/platform/shell/AppSwitcher';
import { appIdForPath } from '@/platform/registry';
import type { AppId, Permission } from '@/types';

/** The guide's tool sidebar: 13rem (assets/opoc.css, .app-shell). */
const RAIL_WIDTH = 208;

interface NavItem {
  label: string;
  to: string;
  Icon: SvgIconComponent;
  requires?: Permission;
  end?: boolean;
}

export interface NavSection {
  heading: string | null;
  /** Set when the section belongs to an application; it renders only inside that application. */
  appId?: AppId;
  items: NavItem[];
}

export const NAV_SECTIONS: NavSection[] = [
  {
    heading: null,
    items: [{ label: 'Home', to: '/', Icon: HomeRounded, end: true }],
  },
  {
    heading: 'Proposal extraction',
    appId: 'proposals',
    items: [
      {
        label: 'Comparisons',
        to: '/proposals',
        Icon: DifferenceRounded,
        end: true,
        requires: 'proposal_job:read',
      },
      {
        label: 'Templates',
        to: '/proposals/templates',
        Icon: TableChartRounded,
        requires: 'proposal_job:read',
      },
      {
        label: 'Read accuracy',
        to: '/proposals/read-accuracy',
        Icon: FactCheckRounded,
        requires: 'proposal_job:read',
      },
    ],
  },
  {
    heading: 'Data trenching',
    appId: 'trenching',
    items: [
      {
        label: 'Engagements',
        to: '/trenching',
        Icon: LayersRounded,
        end: true,
        requires: 'trenching_engagement:read',
      },
      {
        label: 'Templates',
        to: '/trenching/templates',
        Icon: TableChartRounded,
        requires: 'trenching_engagement:read',
      },
    ],
  },
  {
    heading: 'Presentation creation',
    appId: 'presentations',
    items: [
      {
        label: 'Presentations',
        to: '/presentations',
        Icon: SlideshowRounded,
        end: true,
        requires: 'presentation:read',
      },
      {
        label: 'Templates',
        to: '/presentations/html-templates',
        Icon: WebRounded,
        requires: 'presentation:read',
      },
    ],
  },
  {
    heading: 'Employee packet creation',
    appId: 'packets',
    items: [
      {
        label: 'Packets',
        to: '/packets',
        Icon: ArticleRounded,
        end: true,
        requires: 'packet:read',
      },
      {
        label: 'Page library',
        to: '/packets/library',
        Icon: LibraryBooksRounded,
        requires: 'packet:read',
      },
    ],
  },
  {
    heading: 'RFP management',
    appId: 'rfp',
    items: [
      {
        label: 'RFP status',
        to: '/rfp',
        Icon: SpaceDashboardRounded,
        end: true,
        requires: 'rfp:read',
      },
      {
        label: 'RFP requests',
        to: '/rfp/requests',
        Icon: AssignmentRounded,
        requires: 'rfp:read',
      },
      {
        label: 'Workload',
        to: '/rfp/workload',
        Icon: AssignmentIndRounded,
        requires: 'rfp:read',
      },
    ],
  },
  {
    // Employers and carriers belong to Strategic Planning Hub, not to RFP: every application will need them.
    heading: 'Directory',
    items: [
      {
        label: 'Employers',
        to: '/employers',
        Icon: BusinessRounded,
        requires: 'client:read',
      },
      {
        label: 'Carriers',
        to: '/carriers',
        Icon: GroupsRounded,
        requires: 'carrier:read',
      },
    ],
  },
  {
    heading: 'Administration',
    items: [
      { label: 'Users', to: '/admin/users', Icon: PeopleRounded, requires: 'user:read' },
      { label: 'Audit log', to: '/admin/audit', Icon: HistoryRounded, requires: 'audit:read' },
    ],
  },
  {
    // Settings is a personal page — your account, your roles, what this environment is configured
    // to do. Filing it under Administration would be wrong twice: it would mislead about who it is
    // for, and being unrestricted it would keep that heading on screen for people who can reach
    // nothing else beneath it.
    heading: null,
    items: [{ label: 'Settings', to: '/settings', Icon: SettingsRounded }],
  },
];

/**
 * The sections this user should see on this path.
 *
 * Exported and pure so the rules can be tested without rendering a shell. A section with no
 * visible items is dropped entirely — a heading floating above nothing reads as a bug.
 */
export function visibleSections(
  sections: NavSection[],
  can: (permission: Permission) => boolean,
  pathname: string,
): NavSection[] {
  const current = appIdForPath(pathname);
  return sections
    .filter((section) => !section.appId || section.appId === current)
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => !item.requires || can(item.requires)),
    }))
    .filter((section) => section.items.length > 0);
}

export function AppShell(): JSX.Element {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const { user, can, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const sections = useMemo(
    () => visibleSections(NAV_SECTIONS, can, location.pathname),
    [can, location.pathname],
  );

  const handleSignOut = async (): Promise<void> => {
    setMenuAnchor(null);
    await signOut();
    navigate('/sign-in', { replace: true });
  };

  const rail = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', py: 6 }}>
      <Box sx={{ px: 5, mb: 8 }}>
        {/* The white logotype is the correct colourway on the navy field; clear space equals the
            width of the "O", and the lockup never goes below 72px. */}
        <Box
          component="img"
          src="/opoc-primary-white.svg"
          alt="OPOC.us"
          sx={{ width: '6.5rem', display: 'block' }}
        />
        <Typography
          variant="caption"
          sx={{
            color: tint('color-semantic-ink-on-brand', 0.72),
            fontSize: '0.75rem',
            mt: 1,
            display: 'block',
            letterSpacing: 0,
          }}
        >
          Strategic Planning Hub
        </Typography>
      </Box>

      <List sx={{ px: 3, flex: 1 }} component="nav" aria-label="Main">
        {sections.map((section, index) => (
          <Box key={section.heading ?? `section-${index}`} sx={{ mb: 2 }}>
            {section.heading ? (
              <Typography
                variant="overline"
                component="h2"
                sx={{
                  display: 'block',
                  px: 2,
                  pt: 2,
                  pb: 0.5,
                  color: tint('color-semantic-ink-on-brand', 0.72),
                  fontWeight: 600,
                  // All-caps takes zero or positive tracking; the negative body tracking would
                  // close the letters up.
                  letterSpacing: '0.08em',
                  fontSize: '0.6875rem',
                }}
              >
                {section.heading}
              </Typography>
            ) : null}
            {section.items.map(({ label, to, Icon, end }) => (
              <ListItemButton
                key={to}
                component={NavLink}
                to={to}
                end={end ?? false}
                onClick={() => setDrawerOpen(false)}
                sx={{
                  // The guide's sidebar link: a pill, white, a faint wash on hover and a stronger one
                  // for the current page.
                  borderRadius: radius.pill,
                  py: '0.55rem',
                  px: '0.75rem',
                  mb: 0.5,
                  color: semantic.inkOnBrand,
                  '& .MuiListItemIcon-root': { color: 'inherit', minWidth: 34 },
                  '&:hover': {
                    backgroundColor: tint('color-semantic-ink-on-brand', 0.08),
                  },
                  '&.active': {
                    backgroundColor: tint('color-semantic-ink-on-brand', 0.14),
                    color: semantic.inkOnBrand,
                    fontWeight: 600,
                  },
                  '&:focus-visible': {
                    outline: `2px solid ${token('color-semantic-focus-ring-on-brand')}`,
                    outlineOffset: 2,
                  },
                }}
              >
                <ListItemIcon>
                  <Icon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={label}
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    letterSpacing: '-0.03em',
                    fontWeight: 'inherit',
                  }}
                />
              </ListItemButton>
            ))}
          </Box>
        ))}
      </List>
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: surface.sunken,
      }}
    >
      <Drawer
        variant={isDesktop ? 'permanent' : 'temporary'}
        open={isDesktop || drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: isDesktop ? RAIL_WIDTH : 0,
          flexShrink: 0,
          '& .MuiDrawer-paper': { width: RAIL_WIDTH, boxSizing: 'border-box' },
        }}
      >
        {rail}
      </Drawer>

      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <AppBar position="sticky">
          <Toolbar sx={{ gap: 2, minHeight: { xs: 56, md: 64 } }}>
            {!isDesktop ? (
              <IconButton edge="start" onClick={() => setDrawerOpen(true)} aria-label="Open menu">
                <MenuRounded />
              </IconButton>
            ) : null}
            <AppSwitcher />
            <Box sx={{ flex: 1 }} />
            <ReviewQueueButton />
            <PacketReviewQueueButton />
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  textAlign: 'right',
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                <Typography variant="subtitle2" sx={{ lineHeight: 1.2 }}>
                  {user?.displayName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.jobTitle ?? 'OPOC staff'}
                </Typography>
              </Box>
              <Tooltip title="Account">
                <IconButton
                  onClick={(event) => setMenuAnchor(event.currentTarget)}
                  aria-label="Account menu"
                  size="small"
                >
                  <Avatar
                    sx={{
                      width: 34,
                      height: 34,
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      backgroundColor: semantic.interactive,
                      color: semantic.inkOnDark,
                    }}
                  >
                    {user?.initials ?? user?.displayName.slice(0, 2).toUpperCase()}
                  </Avatar>
                </IconButton>
              </Tooltip>
            </Stack>
            <Menu
              anchorEl={menuAnchor}
              open={menuAnchor !== null}
              onClose={() => setMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem disabled sx={{ opacity: '1 !important' }}>
                <Stack>
                  <Typography variant="body2">{user?.email}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {user?.roles.join(', ')}
                  </Typography>
                </Stack>
              </MenuItem>
              <Divider />
              {/* Appearance lives here rather than as its own toolbar button: it is a personal
                  preference, and the account menu is where personal things go. */}
              <ListSubheader
                disableSticky
                sx={{ lineHeight: '32px', fontSize: '0.6875rem', letterSpacing: '0.08em' }}
              >
                APPEARANCE
              </ListSubheader>
              <ColorModeMenuItems onChosen={() => setMenuAnchor(null)} />
              <Divider />
              <MenuItem onClick={handleSignOut}>
                <ListItemIcon>
                  <LogoutRounded fontSize="small" />
                </ListItemIcon>
                Sign out
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        <Box
          component="main"
          key={location.pathname}
          sx={{
            flex: 1,
            px: { xs: 4, md: 8 },
            py: { xs: 6, md: 8 },
            maxWidth: 1600,
            width: '100%',
            mx: 'auto',
          }}
        >
          <Outlet />
        </Box>
        <AppFooter />
      </Box>
    </Box>
  );
}
